import { useCallback, useRef, type KeyboardEvent } from "react";
import { ChevronDown } from "lucide-react";
import QuickMenu, { QuickMenuItem } from "./QuickMenu";

export type FilterOption = { value: string; name: string; count?: number };

/**
 * Keys for a menu of buttons opened from a QuickMenu: arrows, Home and End move between
 * the options, Escape and Tab close it and give focus back to the trigger.
 * QuickMenu's own Escape listener sits on window, which a keydown inside the menu never reaches.
 */
export function menuKeys(e: KeyboardEvent<HTMLElement>, close: () => void, backTo: () => void) {
  if (e.key === "Escape" || e.key === "Tab") {
    if (e.key === "Escape") e.preventDefault();
    close();
    backTo();
    return;
  }
  const items = [...e.currentTarget.querySelectorAll<HTMLButtonElement>("button")];
  const at = items.indexOf(document.activeElement as HTMLButtonElement);
  const next = e.key === "ArrowDown" ? at + 1 : e.key === "ArrowUp" ? at - 1 : e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : null;
  if (next === null) return;
  e.preventDefault();
  // preventScroll: QuickMenu closes on any scroll, including the menu's own.
  items[(next + items.length) % items.length]?.focus({ preventScroll: true });
}

/**
 * One compact dropdown pill for a filter: muted label, current value, chevron ("Category: All ▾").
 * Tinted when it is off its default (the first option). Each option can show how many rows it has.
 */
export default function FilterPill({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}) {
  const anchor = useRef<HTMLSpanElement>(null);
  const current = options.find((o) => o.value === value) || options[0];
  const isSet = Boolean(current) && current.value !== options[0]?.value;
  // Opening moves focus to the chosen option.
  const focusChosen = useCallback((el: HTMLDivElement | null) => {
    el?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus({ preventScroll: true });
  }, []);
  const focusTrigger = () => anchor.current?.querySelector<HTMLButtonElement>("button")?.focus();
  if (!current) return null;
  return (
    <span className="filter-pill" ref={anchor}>
      <QuickMenu
        label={`${label}: ${current.name}`}
        trigger={
          <span className={isSet ? "filter-pill-face is-set" : "filter-pill-face"}>
            <span className="filter-pill-label">{label}</span>
            <span className="filter-pill-value">{current.name}</span>
            <ChevronDown aria-hidden="true" />
          </span>
        }
      >
        {(close) => (
          <div className="filter-pill-menu" ref={focusChosen} onKeyDown={(e) => menuKeys(e, close, focusTrigger)}>
            {options.map((o) => (
              <QuickMenuItem
                key={o.value || "all"}
                active={o.value === current.value}
                onClick={() => { onChange(o.value); close(); focusTrigger(); }}
              >
                <span>{o.name}</span>
                {o.count !== undefined && <span className="filter-pill-count">{o.count}</span>}
              </QuickMenuItem>
            ))}
          </div>
        )}
      </QuickMenu>
    </span>
  );
}
